import { useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CashModal, dateTime, money } from "./BranchCashDialogs";
import { useCashAdvances, type CashAdvance } from "@/hooks/use-cash-advances";
import type { CashBox } from "@/hooks/use-branch-cash";
import { useCashLanguage, workbenchError, workbenchRequest } from "./cashWorkbench";
import {useAuth,hasPermission} from "@/lib/auth";

type LiabilityAdvance = CashAdvance & {
  reference?:string;
  liableEmployeeId?: string | null;
  liableEmployeeName?: string | null;
  liableEmployeeNameAr?: string | null;
  occurredAt?: string | null;
  movedHow?: string | null;
};
type RepaymentResult = {
  employeeId: string;
  cashBoxId: string;
  amount: string;
  remaining: string;
  allocations: { advanceId: string; amount: string; eventId: string; cashMovementId: string }[];
};
type EmployeeDebt = { id: string; name: string; nameAr?: string | null; totalCents: number; advances: LiabilityAdvance[] };
type Attempt = {
  cashBoxId: string;
  employeeId: string;
  amount: string;
  occurredAt: string;
  note: string | null;
  idempotencyKey: string;
};

const cents = (value: string | number | null | undefined) => {
  const match = String(value ?? "").match(/^(\d+)(?:\.(\d{0,2}))?$/);
  if (!match) return 0;
  return Number(match[1]) * 100 + Number((match[2] || "").padEnd(2, "0"));
};
const amountString = (value: number) => `${Math.floor(value / 100)}.${String(value % 100).padStart(2, "0")}`;
const validAmount = (value: string) => /^\d{1,12}(?:\.\d{1,2})?$/.test(value) && cents(value) > 0;

export function AdvancePaidBackDialog({ boxes, close }: { boxes: CashBox[]; close: () => void }) {
  const { ar, text } = useCashLanguage();
  const cashMoney=(value:string|number|null|undefined)=>money(value).replace("EGP",ar?"ج.م":"EGP");
  const localError=(error:unknown)=>{
    const message=workbenchError(error,ar);
    return message.includes(" / ")?message.split(" / ")[ar?message.split(" / ").length-1:0]:message;
  };
  const client = useQueryClient();
  const [boxId, setBoxId] = useState(boxes.length === 1 ? boxes[0].id : "");
  const box = boxes.find(item => item.id === boxId);
  const branchId = box?.branchId || "";
  const query = useCashAdvances(branchId ? [branchId] : []);
  const {user,roles}=useAuth();
  const canRepay=hasPermission(user,"branch_cash.advance_repayment",roles);
  const [employeeId, setEmployeeId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [submittedEmployee, setSubmittedEmployee] = useState<EmployeeDebt | null>(null);
  const [result, setResult] = useState<RepaymentResult | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const employees = useMemo(() => {
    const grouped = new Map<string, EmployeeDebt>();
    for (const raw of query.data?.advances || []) {
      const advance = raw as LiabilityAdvance;
      if (advance.branchId !== branchId || cents(advance.outstandingAmount) <= 0) continue;
      const liableId = advance.liableEmployeeId ?? (advance.movedHow ? null : advance.employeeId);
      if (!liableId) continue;
      const person = query.data?.employees.find(item => item.id === liableId);
      const name = advance.liableEmployeeName || (liableId === advance.employeeId ? advance.employeeName : person?.name) || text("Employee", "موظف");
      const nameAr = advance.liableEmployeeNameAr || null;
      const row = grouped.get(liableId) || { id: liableId, name, nameAr, totalCents: 0, advances: [] };
      row.totalCents += cents(advance.outstandingAmount);
      row.advances.push(advance);
      grouped.set(liableId, row);
    }
    for (const row of grouped.values()) row.advances.sort((a, b) =>
      String(a.occurredAt || a.createdAt).localeCompare(String(b.occurredAt || b.createdAt)) || String(a.createdAt).localeCompare(String(b.createdAt)) || a.id.localeCompare(b.id));
    return [...grouped.values()].sort((a, b) => (ar ? (a.nameAr || a.name) : a.name).localeCompare(ar ? (b.nameAr || b.name) : b.name));
  }, [query.data, branchId, ar, text]);

  const selectedEmployee = attempt && submittedEmployee ? submittedEmployee : employees.find(person => person.id === employeeId);
  const amountCents = validAmount(attempt?.amount || amount) ? cents(attempt?.amount || amount) : 0;
  let previewLeft = amountCents;
  const preview = (selectedEmployee?.advances || []).map(advance => {
    const outstanding = cents(advance.outstandingAmount);
    const allocation = Math.min(outstanding, previewLeft);
    previewLeft -= allocation;
    return { advance, allocation };
  });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || result || !canRepay) return;
    if (!attempt) {
      if (!box || !selectedEmployee || !validAmount(amount)) {
        setError(text("Choose an employee and enter a positive amount with up to two decimal places.", "اختر موظفاً وأدخل مبلغاً موجباً حتى منزلتين عشريتين."));
        return;
      }
      if (cents(amount) > selectedEmployee.totalCents) {
        setError(text("Amount cannot exceed the employee's total outstanding debt in this box.", "لا يمكن أن يتجاوز المبلغ إجمالي الدين المستحق في هذا الصندوق."));
        return;
      }
    }
    setError("");
    setBusy(true);
    const payload = attempt || {
      cashBoxId: box!.id, employeeId: selectedEmployee!.id, amount,
      occurredAt: new Date().toISOString(), note: note.trim() || text("Employee cash repayment", "سداد نقدي لسلفة الموظف"), idempotencyKey: crypto.randomUUID(),
    };
    // Keep the exact attempted body and idempotency key on uncertain outcomes.
    if (!attempt) { setSubmittedEmployee(selectedEmployee!); setAttempt(payload); }
    try {
      const saved = await workbenchRequest<RepaymentResult>("POST", "/api/cash/advances/repay-employee", payload);
      setResult(saved);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["branch-cash"] }),
        client.invalidateQueries({ queryKey: ["/api/staff/bootstrap"] }),
        client.invalidateQueries({ queryKey: ["/api/cash/staff-pay"] }),
      ]);
    } catch (err) {
      setError(localError(err));
    } finally {
      setBusy(false);
    }
  };

  const clearForBox = (id: string) => {
    if (attempt) return;
    setBoxId(id);
    setEmployeeId("");
    setAmount("");
    setError("");
  };
  const title = text("Advance paid back", "سداد سلفة نقداً");
  const locked = !!attempt || busy || !!result;

  return <CashModal title={title} subtitle={text("Record cash physically returned by an employee.", "تسجيل النقدية التي أعادها الموظف فعلياً.")} close={() => !busy && close()} wide>
    <div className="cb-card-pad cc-form min-w-0 max-w-full break-words [&_select]:max-w-full [&_fieldset]:min-w-0" dir={ar ? "rtl" : "ltr"} lang={ar ? "ar" : "en"}>
      {!canRepay?<p role="alert">{text("Advance repayment permission required.","تلزم صلاحية تسجيل سداد السلف.")}</p>:<>
      {result ? <section className="cc-result cc-form" role="status">
        <h3>{text("Cash repayment saved", "تم حفظ السداد النقدي")}</h3>
        <p><b>{ar?(selectedEmployee?.nameAr||selectedEmployee?.name||"—"):(selectedEmployee?.name||"—")}</b> · {cashMoney(result.amount)}</p>
        <p>{text("Receiving box", "الصندوق المستلم")}: {boxes.find(item => item.id === result.cashBoxId)?.branchName || box?.branchName}</p>
        <p>{text("Remaining debt", "الدين المتبقي")}: {cashMoney(result.remaining)}</p>
        <h4>{text("Applied oldest advance first", "تم التوزيع على أقدم السلف أولاً")}</h4>
        {result.allocations.map(allocation => <p key={allocation.eventId}>{text("Advance", "سلفة")} · {selectedEmployee?.advances.find(a=>a.id===allocation.advanceId)?.reference||allocation.advanceId} · {cashMoney(allocation.amount)}</p>)}
        <Button type="button" onClick={close}>{text("Done", "تم")}</Button>
      </section> : <form autoComplete="off" className="cc-form" onSubmit={submit}>
        <div className="cb-context">
          {box && <span>{text("Branch", "الفرع")}<b>{box.branchName}</b></span>}
          <span>{text("Date", "التاريخ")}<b>{dateTime(attempt?.occurredAt || new Date().toISOString())}</b></span>
        </div>
        {boxes.length > 1 && <div className="space-y-2">
          <Label htmlFor="advance-paid-back-box">{text("Receiving cash box", "الصندوق المستلم")}</Label>
          <select id="advance-paid-back-box" className="h-10 w-full rounded-md border bg-background px-3" value={boxId} disabled={locked} onChange={event => clearForBox(event.target.value)}>
            <option value="">{text("Choose a box", "اختر الصندوق")}</option>
            {boxes.map(item => <option key={item.id} value={item.id}>{item.branchName}</option>)}
          </select>
        </div>}
        {box && <>
          {query.isLoading ? <div className="cc-skeleton" role="status" aria-label={text("Loading employees", "جارٍ تحميل الموظفين")} /> : query.isError ?
            <div className="cb-notice cb-error" role="alert">{localError(query.error)} <Button type="button" variant="outline" onClick={() => query.refetch()}>{text("Retry", "إعادة المحاولة")}</Button></div> :
            !employees.length ? <div className="cb-empty">{text("No open employee debts in this branch cash box.", "لا توجد ديون موظفين مستحقة في صندوق هذا الفرع.")}</div> :
            <div className="space-y-2">
              <Label htmlFor="advance-paid-back-employee">{text("Employee", "الموظف")}</Label>
              <select id="advance-paid-back-employee" required className="h-10 w-full rounded-md border bg-background px-3" value={attempt?.employeeId || employeeId} disabled={locked} onChange={event => { setEmployeeId(event.target.value); setAmount(""); setError(""); }}>
                <option value="">{text("Choose an employee", "اختر الموظف")}</option>
                {employees.map(person => <option key={person.id} value={person.id}>{ar ? person.nameAr || person.name : person.name} · {cashMoney(amountString(person.totalCents))}</option>)}
              </select>
            </div>}
        </>}
        {selectedEmployee && !attempt && <section className="cc-result cc-form" aria-live="polite">
          <p>{text("Total owed in this box", "إجمالي المستحق في هذا الصندوق")}: <b>{cashMoney(amountString(selectedEmployee.totalCents))}</b></p>
          <h3>{text("Oldest-first allocation preview", "معاينة التوزيع من الأقدم")}</h3>
          {preview.map(({ advance, allocation }) => <p key={advance.id}>
            {advance.reference} · {dateTime(advance.occurredAt || advance.createdAt)} · {text("Owed", "المستحق")} {cashMoney(advance.outstandingAmount)} · {text("Repayment applied", "المبلغ الموزع")} <b>{cashMoney(amountString(allocation))}</b>
          </p>)}
        </section>}
        {selectedEmployee && <fieldset disabled={locked} className="cc-form">
          <div className="space-y-2"><Label htmlFor="advance-paid-back-amount">{text("Amount (EGP)", "المبلغ (ج.م)")}</Label>
            <Input autoComplete="off" id="advance-paid-back-amount" required inputMode="decimal" value={attempt?.amount || amount} onChange={event => { setAmount(event.target.value); setError(""); }} placeholder="0.00" />
          </div>
          {amountCents > selectedEmployee.totalCents && <p className="cb-notice cb-error" role="alert">{text("Amount exceeds the total owed.", "المبلغ يتجاوز إجمالي الدين.")}</p>}
          <div className="space-y-2"><Label htmlFor="advance-paid-back-note">{text("Note", "ملاحظة")} <span className="text-muted-foreground">{text("Optional", "اختياري")}</span></Label>
            <Textarea autoComplete="off" id="advance-paid-back-note" maxLength={4000} value={attempt?.note || note} onChange={event => setNote(event.target.value)} placeholder={text("Add a short note", "أضف ملاحظة قصيرة")} />
          </div>
        </fieldset>}
        {error && <div className="cb-notice cb-error" role="alert">{error}{attempt && <div>{text("The submitted details are locked. Retry uses the same request key; close only if you want to leave this submission.", "تم قفل البيانات المرسلة. ستستخدم إعادة المحاولة المفتاح نفسه؛ أغلق النافذة فقط إذا أردت مغادرة هذا الطلب.")}</div>}</div>}
        <div className="cb-form-footer">
          <Button type="button" variant="outline" disabled={busy} onClick={close}>{text("Cancel", "إلغاء")}</Button>
          <Button type="submit" disabled={busy || (!attempt && (!box || query.isLoading || query.isError || !selectedEmployee || !validAmount(amount) || amountCents > (selectedEmployee?.totalCents || 0)))}>
            {busy ? text("Saving…", "جارٍ الحفظ…") : attempt ? text("Retry save", "إعادة المحاولة") : text("Save cash repayment", "حفظ السداد النقدي")}
          </Button>
        </div>
      </form>}</>}
    </div>
  </CashModal>;
}

export default AdvancePaidBackDialog;
